<script lang="ts" module>
	export type FilterTypesClasses = {
		body?: string;
	};
</script>

<script lang="ts">
	import classNames from 'classnames';
	import { Accordion, AccordionItem } from '../../../../components/Accordion';
	import { Txt } from '../../../../components/Txt';
	import { isLastItem } from '../../../../utils/isLastItem';
	import type { FilterSection, FilterSections } from '../../types';
	import FilterTypeBody from './FilterTypeBody/FilterTypeBody.svelte';

	interface Props {
		classes?: FilterTypesClasses;
		filterSections: FilterSections;
		withMaxSectionHeight: boolean;
	}

	let { classes = {}, filterSections, withMaxSectionHeight }: Props = $props();

	const HIDDEN_FILTER_SECTION_IDS = ['32']; // todo - remove this when the-beautiful-paintings 'colors' filter is ready

	const shouldShowFilterSection = (filterSection: FilterSection) =>
		filterSection?.id && !HIDDEN_FILTER_SECTION_IDS.includes(filterSection?.id);

	// Estimated px budget for opening every section by default: the drawer's fixed
	// chrome (60px header + 70px footer) plus, per section, a title row and enough
	// list height to be useful. On screens shorter than that, only the first
	// section starts open so the drawer isn't a wall of collapsed-together lists.
	const DRAWER_CHROME_HEIGHT = 130;
	const SECTION_TITLE_HEIGHT = 41;
	const MIN_USEFUL_SECTION_HEIGHT = 120;

	const hasSpaceToOpenAllSections = (sectionCount: number) => {
		if (typeof window === 'undefined') {
			return true;
		}

		const requiredHeight =
			DRAWER_CHROME_HEIGHT + sectionCount * (SECTION_TITLE_HEIGHT + MIN_USEFUL_SECTION_HEIGHT);

		return window.innerHeight >= requiredHeight;
	};

	const visibleFilterSections = $derived(filterSections?.filter(shouldShowFilterSection) ?? []);

	// Evaluated when the accordion items mount, which happens each time the
	// drawer opens — so a resize is picked up on the next open. Besides opening
	// every section by default, this also selects the accordion mode: without
	// space for all sections the accordion is exclusive (single-open), so
	// opening a section closes the previous one instead of stacking.
	const hasSpaceForAllSections = $derived(hasSpaceToOpenAllSections(visibleFilterSections.length));

	const firstAccordionIndex = $derived(
		visibleFilterSections.findIndex((filterSection) => !filterSection?.show_content_only)
	);
</script>

<!-- With a constrained section height the drawer body becomes a full-height flex
     column whose items are the section title rows and bodies directly (the
     accordion wrappers are `display: contents`). Each open body starts from an
     equal share of the space (`grow basis-0`); bodies with less content than
     their share keep their natural height (a measured pixel max-height set by
     FilterTypeBody), handing the leftover to the fuller ones, which scroll
     internally. Keeping the bodies siblings in one flex column is also what lets
     the accordion's `flexGrow` transition reallocate the space smoothly when
     sections open and close. -->
<div class={classNames('flex flex-col', { 'h-full': withMaxSectionHeight })}>
	<Accordion multiple={hasSpaceForAllSections} class="contents">
		{#each visibleFilterSections as filterSection, index}
			{#if filterSection?.show_content_only}
				<FilterTypeBody
					{filterSection}
					{withMaxSectionHeight}
					showBottomBorder={!isLastItem(visibleFilterSections, index)}
					class={classes?.body}
				/>
			{:else}
				<AccordionItem
					dataCy="filter-drawer"
					defaultOpen={hasSpaceForAllSections || index === firstAccordionIndex}
					contentTransition={withMaxSectionHeight ? 'flexGrow' : 'slide'}
					class={withMaxSectionHeight ? 'contents' : 'bg-gray-0'}
					classes={{
						titleButton: 'py-2 px-4 md:px-6 border-b border-gray-200 shrink-0 bg-gray-0',
						contentWrapper: withMaxSectionHeight ? 'contents' : undefined,
						content: withMaxSectionHeight
							? 'flex min-h-0 grow basis-0 flex-col overflow-hidden'
							: undefined
					}}
				>
					{#snippet titleSlot()}
						<Txt variant="label3" data-cy={`filter-drawer-accordion-item-title`}>
							{filterSection?.title}
						</Txt>
					{/snippet}

					<FilterTypeBody
						{filterSection}
						{withMaxSectionHeight}
						clampParentFlexItem={withMaxSectionHeight}
						showBottomBorder={!isLastItem(visibleFilterSections, index)}
						class={classes?.body}
					/>
				</AccordionItem>
			{/if}
		{/each}
	</Accordion>
</div>
